import {ComplianceStatus, CountryCode} from "./types";

export {ComplianceStatus} from "./types";

export interface CompanyMonitorDetails {
  company: CompanyModel;
  companyReportHistory: CompanyReportHistoryItemModel[];
  report: CompanyReportModel | null;
  changeHandlingChanges: CompanyReportChangeModel[];
  events: CompanyEventModel[];
  manuallyHandledRepresentatives: ManualRepresentativeModel[];
}

export enum AmlRiskClass {
  low = 'low',
  normal = 'normal',
  high = 'high',
  unacceptable = 'unacceptable'
}
export interface CompanyModel {
  id: string;
  marketId: string;
  country: string;
  name: string;
  companyRegistrationNumber: string;
  amlRiskClassification: AmlRiskClass | undefined
}

export interface CompanyReportHistoryItemModel {
  id: string;
  date: string;
}

export interface ManualRepresentativeModel {
  id: string;
  givenName: string;
  familyName: string;
  email: string | null;
  dateOfBirth: string | null;
  positions: string[];
  sources?: string[];
  country: string | null;
}

export interface CompanyEventModel {
  id: string;
  eventId: number;
  ruleName: string;
  localEventCode: string;
  globalEventCode: string;
  newValue: string;
  oldValue: string;
  eventDate: string;
}

export interface CompanyReportModel {
  id: string;
  date: string;
  changesSinceLastReport: CompanyReportChangeModel[];
  representatives: CompanyReportRepresentativeModel[];
  
  creditReport: CompanyCreditReportModel;
  amlReport: AmlReportModel;

  country: string | null;
  county: string | null;
  municipality: string | null;
  registeredForFTax: boolean | null;
  registeredForVat: boolean | null;
  registeredForEmployeeTax: boolean | null;
  authorisedSignatures: string | null;
}

export interface AmlReportModel {
  id: string;
  isPep: boolean | null;
  isRca: boolean | null;
  isSanction: boolean;
  report: string;
}

export enum CreditRiskClassification {
  veryLow = 'veryLow',
  low = 'low',
  medium = 'medium',
  high = 'high',
  veryHigh = 'veryHigh'
}
export interface CompanyCreditReportModel {
  id: string;
  report: string;
  creditScore: number | null;
  probabilityOfDefaultPercent: number | null
  totalDebtBalance: number | null
  creditRiskClassification: CreditRiskClassification;
}

export interface CompanyReportRepresentativeModel {
  id: string;
  givenName: string;
  familyName: string;
  email: string | null;
  dateOfBirth: string | null;
  positions: string[];
  sources?: string[];
  country: string | null;
  isPep: boolean;
  isRca: boolean;
  isSanction: boolean;
  amlReport: string;
  manual: boolean;
}

export interface CompanyReportChangeModel {
  id: string;
  date: string;
  description: string;
  needsHandling: boolean;
  handled: boolean;
  handledDate: string | null;
  handledById: string | null;
  comments: CompanyReportChangeCommentModel[];
}

export interface CompanyReportChangeCommentModel {
  id: string;
  commentById: string;
  comment: string;
  commentDate: string;
}

export interface AddRepresentativeModel {
  country?: CountryCode
  dateOfBirth: string
  givenName: string
  familyName: string
  email?: string
  positions: string[]
}

export enum CompanyCheckInitiatorType {
  Internal = 'internal',
  External = 'external'
}

export interface CompanyCheckResponseModel {
  country: string;
  companyRegistrationNumber: string;
  initiatorId: string
  initiatorType: CompanyCheckInitiatorType
  company?: CompanyModel
  report?: CompanyReportModel
  success: boolean
  errorMessage?: string,
  checkedAt: string
}

export interface AmlFlaggedCompanyRepresentativeListItemModel {
  id: string
  companyName: string
  dateOfBirth: string
  country?: CountryCode
  givenName: string
  familyName: string
  positions: string[]
  sources?: string[]
  state: AmlState
  isPep: boolean
  isRca: boolean
  isSanction: boolean
}

export interface AmlFlaggedCompanyListItemModel {
  id: string
  marketId: string
  name: string
  companyRegistrationNumber: string
  state: AmlState
  isSanction: boolean
}

export type AmlState = 'none' | 'flagged' | 'accepted'

export interface AmlDetailsModel {
  state: AmlState
  stateTransitions: AmlStateTransition[]
  manualAccepts: ManualAmlAccept[]
  reports: {}[] //TODO: Can we use a stricter type here? Usage becomes awkward.
}

export interface AmlStateTransition {
  date: string
  from: AmlState
  to: AmlState
}

export interface ManualAmlAccept {
  date: string
  comment: string
}

export interface CompanyRepresentativeAmlDetailsModel {
  id: string
  marketId: string
  companyId: string
  companyRegistrationNumber: string
  companyName: string
  dateOfBirth: string,
  country?: CountryCode
  givenName: string
  familyName: string
  sources?: string[]
  amlDetails: AmlDetailsModel
}

export interface CompanyAmlDetailsModel {
  id: string
  marketId: string
  name: string
  companyRegistrationNumber: string
  amlDetails: AmlDetailsModel
}

export interface ComplianceDecisionRequestModel {
  status: ComplianceStatus
  decidedBy: string | null
  reason: string | null
  investigationReference: string | null
}

export interface UserComplianceListItemModel {
  id: string
  givenName: string
  familyName: string
  identificationNumber: string
  dateOfBirth: string | null
  country: CountryCode | null
  complianceStatus: ComplianceStatus
  initialFlagDate: string | null
  lastDecisionBy: string | null
  score: number | null
  lastDecisionReason: string | null
  lastDecisionInvestigationReference : string | null
}

export interface UserComplianceDetailsResponseModel {
  id: string
  givenName: string
  familyName: string
  identificationNumber: string
  dateOfBirth: string | null
  country: CountryCode | null
  deleted: boolean
  complianceStatus: ComplianceStatus
  initialFlagDate: string | null

  amlSubscriptionTimestamp: string | null
  amlReportTimestamp: string | null

  complianceReportTimestamp: string | null
  complianceReports: UserComplianceReportModel[]
}

export interface UserComplianceReportModel {
  id: string
  amlReport: AmlReportModel
  currentComplianceStatus: ComplianceStatus
  reportedComplianceStatus: ComplianceStatus
  reportedComplianceViolations: string | null
  created: string
  score: number | null
  complianceDecisions: ComplianceDecisionModel[]
}

export interface ComplianceDecisionModel {
  id: string
  status: ComplianceStatus
  decidedById: string
  decidedBy: string
  decidedAt: string
  reason: string
  investigationReference: string
}

export interface AmlReportModel {
  id: string
  created: string
  isPep: boolean | null
  isRca: boolean | null
  isSanction: boolean
  report: string
}
